<template>
  <div vertical style="background: var(--line)">
    <TopBar />

    <!-- ===== MAIN ROW (tiled, 1px hairlines) ===== -->
    <div ref="mainRow" horizontal style="flex: 1; min-height: 0; background: var(--line)">
      <div :style="{ width: assetWidth + 'px', flex: 'none', minWidth: '300px', display: 'flex' }"><AssetList /></div>
      <div class="mv-resizer" @mousedown.prevent="startResize('assetWidth', $event)" title="Drag to resize"></div>
      <div
        ref="playerColumn"
        :vertical="!sideBySide"
        :horizontal="sideBySide"
        :style="{ minWidth: playerColumnMin + 'px' }"
        style="flex: 1; background: var(--line)">
        <div :style="playerPaneStyle"><ClipComponent /></div>
        <div
          :class="sideBySide ? 'mv-resizer' : 'mv-resizer-v'"
          @mousedown.prevent="startResize(sideBySide ? 'playerWidth' : 'playerHeight', $event)"
          @dblclick="sideBySide || resetSplit('playerHeight')"
          :title="sideBySide ? 'Drag to resize' : 'Drag to resize, double-click to fit the player'"></div>
        <div horizontal style="flex: 1; min-width: 0; min-height: 240px; gap: 1px; background: var(--line)">
          <div style="flex: 0.85; min-width: 0; display: flex"><CartList /></div>
          <div style="flex: 1.15; min-width: 300px; display: flex"><CartSubclipList /></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// The player column keeps room for the cart list beside a 300px cart detail.
const PLAYER_COLUMN_MIN = 480;
const HAIRLINE = 1;

// One drag handler for every splitter: its axis, its own minimum, the room the other side keeps,
// and the default share; a split without one keeps its natural size until dragged.
const SPLITS = {
  assetWidth: { key: "mam.assetWidth", axis: "x", min: 300, rest: PLAYER_COLUMN_MIN, share: 0.46 },
  // v2 key: old saves (about 459px) would hide the clip bar
  playerHeight: { key: "mam.v2.playerHeight", axis: "y", min: 220, rest: 240 },
  playerWidth: { key: "mam.playerWidth", axis: "x", min: 440, rest: 620, share: 0.42 },
};

// Narrower than this the player sits above the carts; wider, beside them.
const SIDE_BY_SIDE_AT = 1100;

export default {
  data: () => {
    // playerHeight null: the stacked player takes its natural height
    return { assetWidth: 700, playerHeight: null, playerMaxHeight: 0, playerWidth: 0, sideBySide: false };
  },

  computed: {
    playerColumnMin() {
      return SPLITS.assetWidth.rest;
    },
    playerPaneStyle() {
      if (this.sideBySide) return { width: this.playerWidth + "px", flex: "none", minWidth: SPLITS.playerWidth.min + "px", display: "flex" };
      // natural height, capped so the carts keep their minimum; the player scrolls beyond that
      if (this.playerHeight == null) return { flex: "none", display: "flex", maxHeight: this.playerMaxHeight + "px" };
      return { height: this.playerHeight + "px", flex: "none", minHeight: SPLITS.playerHeight.min + "px", display: "flex" };
    },
  },
  created() {
    window.document.title = "IYP MAM";
  },
  mounted() {
    this.fitSplits();
    window.addEventListener("resize", this.fitSplits);
    document.addEventListener("visibilitychange", this.resyncWhenVisible);

    // the column decides its own orientation, so it keeps working while the asset splitter moves
    this.columnObserver = new ResizeObserver(([entry]) => this.layOutPlayerColumn(entry.contentRect.width));
    this.columnObserver.observe(this.$refs.playerColumn);
  },
  beforeDestroy() {
    this.columnObserver.disconnect();
    window.removeEventListener("resize", this.fitSplits);
    document.removeEventListener("visibilitychange", this.resyncWhenVisible);
  },
  methods: {
    // a hidden tab may have missed events, or the socket slept with it
    resyncWhenVisible() {
      if (document.visibilityState === "visible") this.$store.state.resync();
    },
    /** Re-clamps the saved splits to the room there is now, so a narrower window never overflows. */
    fitSplits() {
      this.restoreSplit("assetWidth", this.$refs.mainRow.clientWidth);
      this.restoreSplit("playerHeight", this.$refs.playerColumn.clientHeight);
      this.playerMaxHeight = this.$refs.playerColumn.clientHeight - SPLITS.playerHeight.rest - HAIRLINE;
    },
    /** Forgets a dragged split (double-click on its splitter). */
    resetSplit(name) {
      localStorage.removeItem(SPLITS[name].key);
      this.fitSplits();
    },
    layOutPlayerColumn(columnWidth) {
      this.sideBySide = columnWidth >= SIDE_BY_SIDE_AT;
      if (!this.sideBySide) return;
      // the column width is only known once it is laid out, so the first pass picks the default
      if (this.playerWidth) this.playerWidth = this.clampSplit("playerWidth", this.playerWidth, columnWidth);
      else this.restoreSplit("playerWidth", columnWidth);
    },
    restoreSplit(name, available) {
      const saved = parseInt(localStorage.getItem(SPLITS[name].key), 10) || 0;
      if (!saved && !SPLITS[name].share) {
        this[name] = null;
        return;
      }
      this[name] = this.clampSplit(name, saved || available * SPLITS[name].share, available);
    },
    clampSplit(name, size, available) {
      const split = SPLITS[name];
      return Math.round(Math.max(split.min, Math.min(available - split.rest - HAIRLINE, size)));
    },
    startResize(name, event) {
      const alongX = SPLITS[name].axis == "x";
      const container = event.target.parentElement;
      const startPointer = alongX ? event.clientX : event.clientY;
      // a natural-height pane starts from what it measures; the splitter always follows its pane
      const pane = event.target.previousElementSibling;
      const startSize = this[name] ?? (alongX ? pane.offsetWidth : pane.offsetHeight);
      const move = (moveEvent) => {
        const available = alongX ? container.clientWidth : container.clientHeight;
        const moved = (alongX ? moveEvent.clientX : moveEvent.clientY) - startPointer;
        this[name] = this.clampSplit(name, startSize + moved, available);
      };
      const stop = () => {
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", stop);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        // a click without a move leaves a natural pane natural
        if (this[name] != null) localStorage.setItem(SPLITS[name].key, this[name]);
      };
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", stop);
      document.body.style.cursor = alongX ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
    },
  },
};
</script>
